// OP1WebAppWindow (build review SEP.23.2026; formerly ShellAppWindow): the
// behavioral shell around one app's Plasmic-designed content. Connects to
// the Desktop State Manager via `windowId`, and provides drag, focus,
// resize, minimize/restore, and close behavior. App content stays fully
// designable in Plasmic through the slot below.
//
// Build review rule: the window HEADER must remain, at all times, inside
// the area of the OP1WebWindowManagerSurface it lives in. The body may hang
// outside the surface (e.g. down over the dock), the header may not. This is
// enforced while dragging, while resizing, and whenever the surface itself
// changes size (browser resize, layout change).
import React, { useCallback, useEffect, useRef } from "react";
import { usePlasmicCanvasContext } from "@plasmicapp/host";
import { useDesktopState, useWindowState } from "./DesktopStateProvider";
import windowRegistry from "./windowRegistry";
import styles from "./OP1WebAppWindow.module.css";

const MIN_WIDTH = 240;
const MIN_HEIGHT = 160;

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

function mergeRefs(...refs) {
  return (node) => {
    refs.forEach((r) => {
      if (typeof r === "function") r(node);
      else if (r) r.current = node;
    });
  };
}

// The surface this window must keep its header inside of. Falls back to the
// positioned ancestor if the window is ever rendered outside a surface.
function getSurface(windowEl) {
  if (!windowEl) return null;
  return windowEl.closest("[data-window-manager-surface]") || windowEl.offsetParent;
}

// Keeps the whole header box inside the surface: x so the header's full
// width fits, y so the header's full height fits. If the window is wider
// than the surface, it pins to the left edge instead of going negative.
function clampHeader({ x, y, width, headerHeight, surfaceWidth, surfaceHeight }) {
  const maxX = Math.max(0, surfaceWidth - width);
  const maxY = Math.max(0, surfaceHeight - headerHeight);
  return { x: clamp(x, 0, maxX), y: clamp(y, 0, maxY) };
}

const OP1WebAppWindow = React.forwardRef(function OP1WebAppWindow(
  { windowId, title, icon, iconAlt, className, children },
  ref
) {
  const { actions } = useDesktopState();
  const win = useWindowState(windowId);
  const dragState = useRef(null);
  const resizeState = useRef(null);
  const windowEl = useRef(null);
  const titleBarEl = useRef(null);
  // Always-current copy of the window state, so the surface ResizeObserver
  // below doesn't need to be torn down and re-created on every drag frame.
  const winRef = useRef(win);
  winRef.current = win;
  // The element only exists once the window state does (we render null
  // before that), so the observer effect must re-run when that flips.
  const hasWin = !!win;
  // Inside Plasmic Studio's design canvas (not Preview, not the live app),
  // always render as open/interactive so content can be selected, dropped
  // into, and edited regardless of the current open/closed runtime state.
  const inCanvas = usePlasmicCanvasContext();

  const handleFocus = useCallback(() => {
    if (windowId) actions.focusWindow(windowId);
  }, [actions, windowId]);

  const measure = useCallback(() => {
    const surface = getSurface(windowEl.current);
    return {
      surfaceWidth: surface ? surface.clientWidth : 0,
      surfaceHeight: surface ? surface.clientHeight : 0,
      // offsetTop includes the window's own 1px border, so the header's
      // BOTTOM edge (not just its height) is what must stay inside.
      headerHeight: titleBarEl.current
        ? titleBarEl.current.offsetTop + titleBarEl.current.offsetHeight
        : 0,
    };
  }, []);

  // Re-clamp when the surface changes size (and once on mount), so a window
  // can't be stranded with its header outside a surface that shrank.
  useEffect(() => {
    if (!windowId || inCanvas) return undefined;
    const surface = getSurface(windowEl.current);
    if (!surface || typeof ResizeObserver === "undefined") return undefined;

    const reclamp = () => {
      const current = winRef.current;
      if (!current) return;
      const { surfaceWidth, surfaceHeight, headerHeight } = measure();
      // A surface with no size (hidden / not laid out yet) says nothing
      // about where windows belong - don't clobber saved positions then.
      if (!surfaceWidth || !surfaceHeight) return;
      const next = clampHeader({
        x: current.x,
        y: current.y,
        width: current.width,
        headerHeight,
        surfaceWidth,
        surfaceHeight,
      });
      if (next.x !== current.x || next.y !== current.y) {
        actions.moveWindow(windowId, next.x, next.y);
      }
    };

    const observer = new ResizeObserver(reclamp);
    observer.observe(surface);
    return () => observer.disconnect();
  }, [actions, windowId, inCanvas, measure, hasWin]);

  const handleTitleBarPointerDown = useCallback(
    (event) => {
      if (!win || !windowId) return;
      handleFocus();
      dragState.current = {
        startX: event.clientX,
        startY: event.clientY,
        originX: win.x,
        originY: win.y,
        width: win.width,
      };
      event.currentTarget.setPointerCapture(event.pointerId);
    },
    [win, windowId, handleFocus]
  );

  const handleTitleBarPointerMove = useCallback(
    (event) => {
      if (!dragState.current || !windowId) return;
      const dx = event.clientX - dragState.current.startX;
      const dy = event.clientY - dragState.current.startY;
      const { surfaceWidth, surfaceHeight, headerHeight } = measure();
      const next = surfaceWidth && surfaceHeight
        ? clampHeader({
            x: dragState.current.originX + dx,
            y: dragState.current.originY + dy,
            width: dragState.current.width,
            headerHeight,
            surfaceWidth,
            surfaceHeight,
          })
        : {
            x: Math.max(0, dragState.current.originX + dx),
            y: Math.max(0, dragState.current.originY + dy),
          };
      actions.moveWindow(windowId, next.x, next.y);
    },
    [actions, windowId, measure]
  );

  const endDrag = useCallback((event) => {
    dragState.current = null;
    try {
      event.currentTarget.releasePointerCapture(event.pointerId);
    } catch {
      // no-op: pointer capture may already be released
    }
  }, []);

  const handleResizePointerDown = useCallback(
    (event) => {
      if (!win || !windowId) return;
      event.stopPropagation();
      handleFocus();
      resizeState.current = {
        startX: event.clientX,
        startY: event.clientY,
        originWidth: win.width,
        originHeight: win.height,
        x: win.x,
      };
      event.currentTarget.setPointerCapture(event.pointerId);
    },
    [win, windowId, handleFocus]
  );

  const handleResizePointerMove = useCallback(
    (event) => {
      if (!resizeState.current || !windowId) return;
      const dx = event.clientX - resizeState.current.startX;
      const dy = event.clientY - resizeState.current.startY;
      const { surfaceWidth } = measure();
      // Width can't grow past the surface's right edge, otherwise the
      // header (which spans the full window width) would leave the surface.
      const maxWidth = surfaceWidth
        ? Math.max(MIN_WIDTH, surfaceWidth - resizeState.current.x)
        : Infinity;
      actions.resizeWindow(
        windowId,
        clamp(resizeState.current.originWidth + dx, MIN_WIDTH, maxWidth),
        Math.max(MIN_HEIGHT, resizeState.current.originHeight + dy)
      );
    },
    [actions, windowId, measure]
  );

  const endResize = useCallback((event) => {
    resizeState.current = null;
    try {
      event.currentTarget.releasePointerCapture(event.pointerId);
    } catch {
      // no-op: pointer capture may already be released
    }
  }, []);

  const mergedRef = useCallback(
    (node) => mergeRefs(windowEl, ref)(node),
    [ref]
  );

  // No windowId set yet - show a friendly placeholder in Studio instead
  // of silently rendering nothing, so it's obvious a prop is missing.
  if (!windowId) {
    return (
      <div ref={mergedRef} className={[styles.window, className].filter(Boolean).join(" ")}>
        <div className={styles.placeholder}>
          Set a &quot;Window id&quot; prop (e.g. &quot;conversations&quot;, &quot;notes&quot;)
        </div>
      </div>
    );
  }

  if (!win) return null;

  const displayTitle = title || windowRegistry[windowId]?.title || windowId;
  const isVisible = inCanvas ? true : win.isOpen && !win.isMinimized;

  return (
    <div
      ref={mergedRef}
      className={[styles.window, win.isFocused ? styles.focused : "", className]
        .filter(Boolean)
        .join(" ")}
      style={{
        display: "flex",
        visibility: isVisible ? "visible" : "hidden",
        left: win.x,
        top: win.y,
        width: win.width,
        height: win.height,
        zIndex: win.zIndex,
      }}
      onPointerDownCapture={handleFocus}
    >
      <div
        ref={titleBarEl}
        className={styles.titleBar}
        onPointerDown={handleTitleBarPointerDown}
        onPointerMove={handleTitleBarPointerMove}
        onPointerUp={endDrag}
      >
        <div className={styles.titleLeft}>
          {icon ? (
            <img
              src={icon}
              alt={iconAlt || ""}
              className={styles.icon}
              draggable={false}
            />
          ) : null}
          <span className={styles.titleText}>{displayTitle}</span>
        </div>
        {/* stopPropagation on pointerdown here is load-bearing: without it,
            the titleBar's own onPointerDown (above) fires first as the event
            bubbles up, arms window-drag state, and calls setPointerCapture()
            on the titleBar - which silently retargets the click that would
            otherwise land on these buttons, so minimize/close never fire. */}
        <div
          className={styles.titleBarButtons}
          onPointerDown={(event) => event.stopPropagation()}
        >
          <button
            type="button"
            className={styles.minimizeButton}
            aria-label="Minimize"
            onClick={(event) => {
              event.stopPropagation();
              actions.minimizeWindow(windowId);
            }}
          >
            &#8211;
          </button>
          <button
            type="button"
            className={styles.closeButton}
            aria-label="Close"
            onClick={(event) => {
              event.stopPropagation();
              actions.closeWindow(windowId);
            }}
          >
            &#215;
          </button>
        </div>
      </div>
      <div className={styles.content}>{children}</div>
      <div
        className={styles.resizeHandle}
        onPointerDown={handleResizePointerDown}
        onPointerMove={handleResizePointerMove}
        onPointerUp={endResize}
      />
    </div>
  );
});

export default OP1WebAppWindow;
